import { t } from "i18next";
import { Suspense, useCallback, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import QueryBuilder, { getFirstOption, standardClassnames, useValueEditor } from "react-querybuilder";
import "react-querybuilder/dist/query-builder.css";
import Checkbox from "./Checkbox";
import DateInput from "./DateInput";
import { Col, Row } from "./Grid";
import Icon from "./Icon";
import NumericInput from "./NumericInput";
import Select from "./Select";
import TagManager from "./TagManager";
import TextInput from "./TextInput";
import TextareaInput from "./TextareaInput";
import TimeInput from "./TimeInput";

const bootstrapControlClassnames = {
    addGroup: 'btn btn-secondary btn-sm',
    addRule: 'btn btn-primary btn-sm',
    cloneGroup: 'btn btn-secondary btn-sm',
    cloneRule: 'btn btn-secondary btn-sm',
    lockGroup: 'btn btn-secondary btn-sm',
    lockRule: 'btn btn-secondary btn-sm',
    removeGroup: 'btn btn-danger btn-sm',
    removeRule: 'btn btn-danger btn-sm',
}

export const defaultOperators = t => [
    { name: '=', value: '=', label: '=' },
    { name: '!=', value: '!=', label: '!=' },
    { name: '<', value: '<', label: '<' },
    { name: '>', value: '>', label: '>' },
    { name: '<=', value: '<=', label: '<=' },
    { name: '>=', value: '>=', label: '>=' },
    { name: 'contains', value: 'contains', label: t('contains') },
    { name: 'beginsWith', value: 'beginsWith', label: t('beginsWith') },
    { name: 'endsWith', value: 'endsWith', label: t('endsWith') },
    { name: 'doesNotContain', value: 'doesNotContain', label: t('doesNotContain') },
    { name: 'doesNotBeginWith', value: 'doesNotBeginWith', label: t('doesNotBeginWith') },
    { name: 'doesNotEndWith', value: 'doesNotEndWith', label: t('doesNotEndWith') },
    { name: 'null', value: 'null', label: t('isNull') },
    { name: 'notNull', value: 'notNull', label: t('isNotNull') },
    { name: 'in', value: 'in', label: t('in') },
    { name: 'notIn', value: 'notIn', label: t('notIn') },
    { name: 'between', value: 'between', label: t('between') },
    { name: 'notBetween', value: 'notBetween', label: t('notBetween') }
];

export const CustomValueEditor = ({ ...allProps }) => {
    const {
        operator,
        value,
        handleOnChange,
        title,
        className,
        type = 'text',
        inputType = 'text',
        values = [],
        listsAsArrays,
        parseNumbers,
        fieldData,
        disabled,
        separator = null,
        testID,
        selectorComponent,
        ...props
    } = allProps;

    const { valueAsArray, multiValueHandler } = useValueEditor(allProps);

    const makeControl = useCallback((inputTypeCoerced, className, value, handleOnChange, placeHolderText, fieldData, disabled, props) => {
        switch (inputTypeCoerced) {
            case 'date':
                return <DateInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText} hideLabel={true} tz={fieldData.tz} disabled={disabled} {...props}></DateInput>
            case 'time':
                return <TimeInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText} hideLabel={true} tz={fieldData.tz} disabled={disabled} {...props}></TimeInput>
            case 'timestamp':
                return <Row>
                    <Col>
                        <DateInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText + t('date')} hideLabel={true} tz={fieldData.tz} disabled={disabled} {...props}></DateInput>
                    </Col>
                    <Col>
                        <TimeInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText + t('time')} hideLabel={true} tz={fieldData.tz} disabled={disabled} {...props}></TimeInput>
                    </Col>
                </Row>
            case 'number':
                return <NumericInput className={className} value={value} onChange={v => {handleOnChange(v)}} placeholder={placeHolderText} hideLabel={true} disabled={disabled} {...props}></NumericInput>
            default:
                return <TextInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText} hideLabel={true} disabled={disabled} {...props}></TextInput>
        }
    }, []);

    if (operator === 'null' || operator === 'notNull') {
        return null;
    }

    const placeHolderText = fieldData?.placeholder ?? '';
    const inputTypeCoerced = ['in', 'notIn'].includes(operator) ? 'text' : inputType || 'text';

    if ((operator === 'between' || operator === 'notBetween') &&
        (inputTypeCoerced === 'select' || inputTypeCoerced === 'text')) {
            const editors = ['from', 'to'].map((key, i) => {
                if (type === 'select' || type === 'multiselect') {
                    return <selectorComponent
                        {...props}
                        schema={allProps.schema}
                        key={key}
                        className={standardClassnames.valueListItem}
                        handleOnChange={v => multiValueHandler(v, i)}
                        disabled={disabled}
                        value={valueAsArray[i] ?? getFirstOption(values)}
                        options={values}
                        listsAsArrays={listsAsArrays}
                    />
                }
                return makeControl(inputTypeCoerced, placeHolderText, valueAsArray[i] ?? '', v => multiValueHandler(v, i), placeHolderText, fieldData, disabled, props);
              });
          
              return (
                <span data-testid={testID} className={className} title={title}>
                  {editors[0]}
                  {separator}
                  {editors[1]}
                </span>
              );
    }

    switch (type) {
        case 'select':
        case 'multiselect':
            return <selectorComponent
                {...props}
                schema={allProps.schema}
                testID={testID}
                className={className}
                title={title}
                handleOnChange={handleOnChange}
                disabled={disabled}
                value={value}
                options={values}
                multiple={type === 'multiselect'}
                listsAsArrays={listsAsArrays}
                />
        case 'textarea':
            return <TextareaInput className={className} value={value} onChange={handleOnChange} placeholder={placeHolderText} hideLabel={true} disabled={disabled} {...props}></TextareaInput>
        case 'switch':
        case 'checkbox':
            return <Checkbox className={className} checked={!!value} placeholder={placeHolderText} hideLabel={true} disabled={disabled} {...props}></Checkbox>
        case 'radio':
            break;
        default:
            break;       
    }

    return makeControl(inputTypeCoerced, className, value, handleOnChange, placeHolderText, fieldData, disabled, props);
}

const CustomValueSelector = ({ value, placeholder, handleOnChange, options, disabled, multiple, listsAsArrays }) => {
    const processChange = v => {
        if (listsAsArrays) {
            handleOnChange(JSON.stringify(v));
        } else {
            handleOnChange(v.map(v => v.toString()).join(','));
        }
    }
    if (multiple) {
        return <TagManager tags={value} values={options} placeholder={placeholder} onChange={processChange} disabled={disabled}></TagManager>
    } else {
        return <Select value={value} placeholder={placeholder} onChange={handleOnChange} disabled={disabled} options={options} labelField='label' valueField='name'></Select>
    }
}

export const ConditionBuilder = ({ valueEditor, getOperators, query, onQueryChange, ...props }) => {
    const { t } = useTranslation('querybuilder');
    const translations = useMemo(() => ({
        addGroup: { label: <><Icon intent="add"></Icon> {t('group')}</>, title: t('addGroup') },
        removeGroup: { label: <Icon intent="delete"></Icon>, title: t('removeGroup') },
        addRule: { label: <><Icon intent="add"></Icon> {t('rule')}</>, title: t('addRule') },
        removeRule: { label: <Icon intent="delete"></Icon>, title: t('removeRule') },
        cloneRule: { label: <Icon intent="copy"></Icon>, title: t('cloneRule') },
        cloneRuleGroup: { label: <Icon intent="copy"></Icon>, title: t('cloneGroup') },
        dragHandle: { label: <i className="bi bi-grip-vertical" /> },
        lockGroup: { label: <Icon intent="unlocked"></Icon>, title: t('lockGroup') },
        lockRule: { label: <Icon intent="unlocked"></Icon>, title: t('lockRule') },
        lockGroupDisabled: { label: <Icon intent="locked"></Icon>, title: t('unlockGroup') },
        lockRuleDisabled: { label: <Icon intent="locked"></Icon>, title: t('unlockRule') },
        shiftActionUp: { label: <Icon intent="up"></Icon>, title: t('shiftUp') },
        shiftActionDown: { label: <Icon intent="down"></Icon>, title: t('shiftDown') },
        fields: { title: t('fields') },
        operators: { title: t('operators') },
        value: { title: t('value') },
        combinators: { title: t('combinators') },
        notToggle: { label: t('not'), title: t('invertTitle') },
        valueSourceSelector: { title: t('valueSource') }
    }), [t]);
    const operators = useMemo(() => defaultOperators(t), [t]);
    const combinators = useMemo(() => [
        { name: 'and', value: 'and', label: t('logicalAND') },
        { name: 'or', value: 'or', label: t('logicalOR') },
    ], [t]);

    const getOps = useCallback((fieldName, { fieldData }) => {
        const result = getOperators?.(fieldName, { fieldData });
        if (result) {
            return result;
        }
        switch (fieldData.inputType) {
            case 'number':
            case 'date':
            case 'timestamp':
                return operators.filter(op => ['=','!=','<','<=','>','>=','null','notNull'].includes(op.name));
            default:
                return operators.filter(op => ['=','!=','contains','beginsWith','endsWith','doseNotContain','doesNotBeginWith','doseNotEndWith','null','notNull','in','notIn'].includes(op.name));
        }
    }, [getOperators, operators]);

    // const parsedQuery = useMemo(() => query ? parseJsonLogic(query) : query, [query]);

    // const handleQueryChange = useCallback(query => {
    //     onQueryChange(query ? formatQuery(query, 'jsonlogic') : query);
    // }, [onQueryChange]);
    
    return <Suspense fallback={<Spinner animation="border" variant="primary"></Spinner>}><QueryBuilder listsAsArrays={true} controlElements={{ valueEditor: valueEditor ?? CustomValueEditor, valueSelector: CustomValueSelector }} controlClassnames={bootstrapControlClassnames} combinators={combinators} getOperators={getOps} translations={translations} query={query} onQueryChange={onQueryChange} {...props}></QueryBuilder></Suspense>
}